// Competency Explorer data layer
// Powers the cascading Domain → Curricular Goal → Competency (indicator) drill.
//
// Data availability:
//  • Class-level records carry per-CG rubric maps (term1_competency / term2_competency).
//    → full cascade: domain, CG and indicator level, Entry vs Exit.
//  • National / Region / School records carry only aggregate domain scores.
//    → domain-level distribution only (current term); CG/indicator require class drill.

import {
  parseCompetencyMap,
  getDomainScore,
  FOUNDATIONAL_SCHEMA,
  CurricularGoal,
  CGIndicator,
} from './foundationalSchema';
import {
  AnalyticsStudentRecord,
  TermFilter,
  scaleScore,
  getFoundationCompetencyLevel,
} from '../../../services/holisticApi';

export type FoundationLevel = 'Beginner' | 'Progressing' | 'Proficient';

export const LEVEL_COLORS: Record<FoundationLevel, string> = {
  Proficient:  '#10B981',
  Progressing: '#F59E0B',
  Beginner:    '#EF4444',
};

// ── canonical 6 NCF domains (aligned with both rubric CGs and aggregate score fields) ──
export interface ExplorerDomain {
  id: string;
  label: string;
  shortLabel: string;
  icon: string;
  cgNumbers: number[];
  scoreField: keyof AnalyticsStudentRecord;
  accent: string;
  soft: string; // tailwind gradient classes
}

export const EXPLORER_DOMAINS: ExplorerDomain[] = [
  { id: 'physical',  label: 'Physical Development',        shortLabel: 'Physical',   icon: '🤸', cgNumbers: [1, 2, 3],     scoreField: 'physical_dev_score',    accent: '#10B981', soft: 'from-emerald-50 to-teal-50' },
  { id: 'socio',     label: 'Socio-Emotional & Ethical',  shortLabel: 'Socio-Emo',  icon: '🤝', cgNumbers: [4, 5, 6],     scoreField: 'socio_emotional_score', accent: '#7C3AED', soft: 'from-violet-50 to-purple-50' },
  { id: 'cognitive', label: 'Cognitive Development',       shortLabel: 'Cognitive',  icon: '🧠', cgNumbers: [7],           scoreField: 'cognitive_score',       accent: '#6366F1', soft: 'from-indigo-50 to-violet-50' },
  { id: 'language',  label: 'Language & Literacy',         shortLabel: 'Language',   icon: '📖', cgNumbers: [8, 9, 10, 11], scoreField: 'language_score',        accent: '#F59E0B', soft: 'from-amber-50 to-orange-50' },
  { id: 'aesthetic', label: 'Aesthetic & Cultural',        shortLabel: 'Arts',       icon: '🎨', cgNumbers: [12],          scoreField: 'aesthetic_score',       accent: '#F43F5E', soft: 'from-rose-50 to-pink-50' },
  { id: 'habits',    label: 'Positive Learning Habits',    shortLabel: 'Habits',     icon: '📚', cgNumbers: [13],          scoreField: 'learning_habits_score', accent: '#0EA5E9', soft: 'from-sky-50 to-cyan-50' },
];

// ── B/P/Pr distribution primitive ────────────────────────────────────────────
export interface Distribution {
  beginner: number;
  progressing: number;
  proficient: number;
  total: number;
  bPct: number;
  pPct: number;
  prPct: number;
  avgPct: number;
  level: FoundationLevel;
}

export const EMPTY_DIST: Distribution = {
  beginner: 0, progressing: 0, proficient: 0, total: 0,
  bPct: 0, pPct: 0, prPct: 0, avgPct: 0, level: 'Beginner',
};

const pct = (n: number, t: number) => (t ? +((n / t) * 100).toFixed(1) : 0);

/** Build a B/P/Pr distribution from an array of per-student 0-100 scores. */
export const buildDistribution = (scores: number[]): Distribution => {
  const valid = scores.filter(s => s > 0);
  const total = valid.length;
  if (!total) return EMPTY_DIST;
  let b = 0, p = 0, pr = 0, sum = 0;
  valid.forEach(s => {
    sum += s;
    const lvl = getFoundationCompetencyLevel(s);
    if (lvl === 'Proficient') pr++;
    else if (lvl === 'Progressing') p++;
    else b++;
  });
  const avgPct = +(sum / total).toFixed(1);
  return {
    beginner: b, progressing: p, proficient: pr, total,
    bPct: pct(b, total), pPct: pct(p, total), prPct: pct(pr, total),
    avgPct, level: getFoundationCompetencyLevel(avgPct),
  };
};

/** Build a distribution directly from rubric-level counts (1/2/3). */
export const distFromRubricCounts = (b: number, p: number, pr: number): Distribution => {
  const total = b + p + pr;
  if (!total) return EMPTY_DIST;
  // avg rubric → pct
  const avgPct = +(((1 * b + 2 * p + 3 * pr) / total / 3) * 100).toFixed(1);
  return {
    beginner: b, progressing: p, proficient: pr, total,
    bPct: pct(b, total), pPct: pct(p, total), prPct: pct(pr, total),
    avgPct, level: getFoundationCompetencyLevel(avgPct),
  };
};

// ── rubric availability ───────────────────────────────────────────────────────
export const hasRubricData = (records: AnalyticsStudentRecord[]): boolean =>
  records.some(r =>
    Object.keys(parseCompetencyMap(r.term1_competency)).length > 0 ||
    Object.keys(parseCompetencyMap(r.term2_competency)).length > 0,
  );

// ── per-student domain score (rubric-preferred, aggregate fallback) ───────────
const domainScoreForTerm = (
  r: AnalyticsStudentRecord,
  domain: ExplorerDomain,
  term: TermFilter,
): number => {
  const m1 = parseCompetencyMap(r.term1_competency);
  const m2 = parseCompetencyMap(r.term2_competency);
  const hasRubric = Object.keys(m1).length > 0 || Object.keys(m2).length > 0;
  if (hasRubric) {
    if (term === 1) return getDomainScore(domain.cgNumbers, m1);
    if (term === 2) return getDomainScore(domain.cgNumbers, m2);
    const s1 = getDomainScore(domain.cgNumbers, m1);
    const s2 = getDomainScore(domain.cgNumbers, m2);
    return s1 && s2 ? (s1 + s2) / 2 : s1 || s2;
  }
  return scaleScore(r[domain.scoreField]);
};

// ── single CG score for one student in one term-map (0-100) ───────────────────
const cgScoreFromMap = (cg: CurricularGoal, map: Record<string, number>): number => {
  let sum = 0, cnt = 0;
  cg.assessment.forEach(ind => {
    const s = map[ind.entryKey];
    if (s === 1 || s === 2 || s === 3) { sum += s; cnt++; }
  });
  return cnt ? +(((sum / cnt) / 3) * 100).toFixed(1) : 0;
};

// ══════════════════════════════════════════════════════════════════════════════
//  LEVEL 1 — Domains
// ══════════════════════════════════════════════════════════════════════════════
export interface DomainCard {
  domain: ExplorerDomain;
  current: Distribution;          // selected-term distribution
  entry: Distribution;            // rubric only
  exit: Distribution;             // rubric only
  hasEntryExit: boolean;
  movement: number;               // exit prPct - entry prPct
}

export const getDomainCards = (
  records: AnalyticsStudentRecord[],
  term: TermFilter,
): DomainCard[] => {
  const rubric = hasRubricData(records);
  return EXPLORER_DOMAINS.map(domain => {
    const current = buildDistribution(records.map(r => domainScoreForTerm(r, domain, term)));
    let entry = EMPTY_DIST, exit = EMPTY_DIST, hasEntryExit = false;
    if (rubric) {
      entry = buildDistribution(records.map(r => getDomainScore(domain.cgNumbers, parseCompetencyMap(r.term1_competency))));
      exit  = buildDistribution(records.map(r => getDomainScore(domain.cgNumbers, parseCompetencyMap(r.term2_competency))));
      hasEntryExit = entry.total > 0 || exit.total > 0;
    }
    return {
      domain, current, entry, exit, hasEntryExit,
      movement: +(exit.prPct - entry.prPct).toFixed(1),
    };
  });
};

// ══════════════════════════════════════════════════════════════════════════════
//  LEVEL 2 — Curricular Goals within a domain
// ══════════════════════════════════════════════════════════════════════════════
export interface CGCard {
  cg: CurricularGoal;
  current: Distribution;
  entry: Distribution;
  exit: Distribution;
  movement: number;
}

export const getCGCards = (
  records: AnalyticsStudentRecord[],
  domain: ExplorerDomain,
  term: TermFilter,
): CGCard[] => {
  const cgs = FOUNDATIONAL_SCHEMA.filter(cg => domain.cgNumbers.includes(cg.s_no));
  return cgs.map(cg => {
    const curScores = records.map(r => {
      const m1 = parseCompetencyMap(r.term1_competency);
      const m2 = parseCompetencyMap(r.term2_competency);
      if (term === 1) return cgScoreFromMap(cg, m1);
      if (term === 2) return cgScoreFromMap(cg, m2);
      const a = cgScoreFromMap(cg, m1), b = cgScoreFromMap(cg, m2);
      return a && b ? (a + b) / 2 : a || b;
    });
    const entry = buildDistribution(records.map(r => cgScoreFromMap(cg, parseCompetencyMap(r.term1_competency))));
    const exit  = buildDistribution(records.map(r => cgScoreFromMap(cg, parseCompetencyMap(r.term2_competency))));
    return {
      cg,
      current: buildDistribution(curScores),
      entry, exit,
      movement: +(exit.prPct - entry.prPct).toFixed(1),
    };
  });
};

// ══════════════════════════════════════════════════════════════════════════════
//  LEVEL 3 — Indicators (competencies) within a CG
// ══════════════════════════════════════════════════════════════════════════════
export interface IndicatorComparison {
  indicator: CGIndicator;
  entry: Distribution;
  exit: Distribution;
  movement: number;
}

const indicatorDist = (
  records: AnalyticsStudentRecord[],
  key: string,
  field: 'term1_competency' | 'term2_competency',
): Distribution => {
  let b = 0, p = 0, pr = 0;
  records.forEach(r => {
    const s = (parseCompetencyMap(r[field]) as Record<string, number>)[key];
    if (s === 3) pr++; else if (s === 2) p++; else if (s === 1) b++;
  });
  return distFromRubricCounts(b, p, pr);
};

export const getIndicatorComparisons = (
  records: AnalyticsStudentRecord[],
  cg: CurricularGoal,
): IndicatorComparison[] =>
  cg.assessment.map(indicator => {
    const entry = indicatorDist(records, indicator.entryKey, 'term1_competency');
    const exit  = indicatorDist(records, indicator.entryKey, 'term2_competency');
    return { indicator, entry, exit, movement: +(exit.prPct - entry.prPct).toFixed(1) };
  });

// ── class discovery from a record set ────────────────────────────────────────
export interface ClassOption { classId: number; count: number; }

export const getClassOptions = (records: AnalyticsStudentRecord[]): ClassOption[] => {
  const map = new Map<number, number>();
  records.forEach(r => {
    const id = Number(r.class_id);
    if (!Number.isFinite(id) || id === 0) return;
    map.set(id, (map.get(id) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([classId, count]) => ({ classId, count }))
    .sort((a, b) => a.classId - b.classId);
};
